import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { erpApi } from '../../api/erp';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input, Select } from '../../components/ui/Input';
import './erp.css';

interface CreatedAgent {
  email: string;
  password: string;
  referralCode: string;
  emailSent: boolean;
  emailError?: string;
}

export function ErpCreateAgentPage() {
  const [form, setForm] = useState({
    email: '',
    name: '',
    surname: '',
    current_level: 'bronze',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState<CreatedAgent | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await erpApi.createAgent(form);
      setCreated({
        email: result.agent.email,
        password: result.provisional_password,
        referralCode: result.agent.referral_code,
        emailSent: result.email_sent,
        emailError: result.email_error,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el agente');
    } finally {
      setLoading(false);
    }
  }

  async function copyPassword() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // El navegador puede bloquear el portapapeles; el admin siempre puede copiar a mano.
    }
  }

  function reset() {
    setForm({ email: '', name: '', surname: '', current_level: 'bronze' });
    setCreated(null);
    setCopied(false);
  }

  return (
    <div>
      <p style={{ marginBottom: '1rem' }}>
        <Link to="/erp/agentes">← Volver a agentes</Link>
      </p>
      <Card title="Nuevo agente">
        {created ? (
          <div className="erp-form-grid">
            <p style={{ color: 'var(--color-success)', margin: 0 }}>Agente creado correctamente.</p>
            <p style={{ margin: 0 }}>
              Contraseña provisional para <strong>{created.email}</strong>. Se mostrará
              <strong> una sola vez</strong>: cópiala ahora si la necesitas.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <code
                style={{
                  fontSize: '1.05rem',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.4rem',
                  background: 'var(--color-surface-2, rgba(0,0,0,0.06))',
                  letterSpacing: '0.03em',
                }}
              >
                {created.password}
              </code>
              <Button type="button" size="sm" variant="ghost" onClick={copyPassword}>
                {copied ? 'Copiada ✓' : 'Copiar'}
              </Button>
            </div>
            <p className="muted" style={{ margin: 0, fontSize: '0.9rem' }}>
              {created.emailSent
                ? 'Se ha enviado también por email al agente.'
                : `No se pudo enviar el email al agente${
                    created.emailError ? ` (${created.emailError})` : ''
                  }. Comunícale la contraseña manualmente.`}
            </p>
            <p className="muted" style={{ margin: 0, fontSize: '0.9rem' }}>
              El agente deberá cambiarla en su primer inicio de sesión. Código de referido:{' '}
              <strong>{created.referralCode}</strong>.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button type="button" onClick={reset}>
                Crear otro agente
              </Button>
              <Link to="/erp/agentes">
                <Button type="button" variant="ghost">
                  Ir a agentes
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <form className="erp-form-grid" onSubmit={handleSubmit}>
            <div className="grid-2">
              <Input
                label="Nombre"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
              />
              <Input
                label="Apellidos"
                value={form.surname}
                onChange={(e) => setForm((p) => ({ ...p, surname: e.target.value }))}
                required
              />
            </div>
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              required
              hint="Se generará una contraseña provisional segura y se enviará a este correo"
            />
            <Select
              label="Nivel inicial"
              value={form.current_level}
              onChange={(e) => setForm((p) => ({ ...p, current_level: e.target.value }))}
              options={[
                { value: 'bronze', label: 'Bronce' },
                { value: 'silver', label: 'Plata' },
                { value: 'gold', label: 'Oro' },
                { value: 'diamond', label: 'Diamante' },
              ]}
            />
            {error ? <p style={{ color: 'var(--color-danger)', margin: 0 }}>{error}</p> : null}
            <Button type="submit" disabled={loading}>
              {loading ? 'Creando…' : 'Crear agente'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
